import React, { useState } from "react";
import {
  FaPlus,
  FaInfoCircle,
  FaClock,
  FaCalendarAlt,
  FaPen,
} from "react-icons/fa";
import { FiCheck, FiTrash2, FiMinus, FiPlus, FiX } from "react-icons/fi";
import PetServiceCategoryDrawer from "../Orders/PetServiceCategoryDrawer";

// Formats an ISO date string (or any parseable date) to dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ServiceDetailsCard = ({
  serviceCategory,
  serviceDetails,
  staffName,
  boardingOrderItem,
  addons,
  products,
  otherCharges,
  onOpenProductsDrawer,
  onOpenAddonsDrawer,
  onOpenOtherChargesDrawer,
  onDeleteAddon,
  onDeleteProduct,
  onIncrementProduct,
  onDecrementProduct,
  onDeleteOtherCharge,

  // ── New: everything needed to self-host the edit drawer ────────────────
  rawData, // the raw appointment/order row (ref_id, service_id, pet_type_id, etc.)
  orderId,
  clientId,
  clientName,
  clientPhone,
  vendorPetId,
  onServiceUpdated, // (updated) => void — parent syncs its serviceDetails/petDetails state
  readOnly = false,
}) => {
  const [editPetServiceOpen, setEditPetServiceOpen] = useState(false);

  const addonsTotal = addons.reduce(
    (sum, a) => sum + parseFloat(a.addon_price || a.price || 0),
    0,
  );
  const productsTotal = products.reduce((sum, p) => {
    const effectivePrice = parseFloat(
      p.effective_sale_price ||
        p.unit_price ||
        p.price ||
        p.sale_price ||
        p.mrp ||
        p.total_price / (p.quantity || 1) ||
        0,
    );
    const quantity = p.quantity || 1;
    return sum + (isNaN(effectivePrice) ? 0 : effectivePrice * quantity);
  }, 0);
  const chargesTotal = otherCharges.reduce(
    (sum, c) => sum + parseFloat(c.amount || 0),
    0,
  );
  const totalCharges = addonsTotal + productsTotal + chargesTotal;

  const canEditService = !readOnly && !!(rawData && orderId);
  const isBoarding =
    String(serviceCategory || "").toLowerCase() === "boarding" ||
    String(rawData?.item_type || "").toLowerCase() === "boarding" ||
    String(rawData?.service_type_name || "").toLowerCase() === "boarding" ||
    String(boardingOrderItem?.item_type || "").toLowerCase() === "boarding";

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-2 py-3">
        {/* Header row: category | date | time — left; action buttons — right */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-gray-900">
            {serviceCategory}

            {isBoarding ? (
              <>
                {(boardingOrderItem?.start_date ||
                  serviceDetails.checkInDate) && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">|</span> Check
                    In:{" "}
                    {formatDate(
                      boardingOrderItem?.start_date ||
                        serviceDetails.checkInDate,
                    )}
                  </>
                )}

                {(boardingOrderItem?.end_date ||
                  serviceDetails.checkOutDate) && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">|</span> Check
                    Out:{" "}
                    {formatDate(
                      boardingOrderItem?.end_date ||
                        serviceDetails.checkOutDate,
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {serviceDetails.date && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">
                      |
                    </span> Date: {formatDate(serviceDetails.date)}
                  </>
                )}

                {serviceDetails.time && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">
                      |
                    </span> Time: {serviceDetails.time}
                  </>
                )}
              </>
            )}
          </h3>
          <div className="flex items-center gap-4 flex-wrap">
            {!readOnly && (
              <>
                <button
                  onClick={onOpenProductsDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Products
                </button>
                <button
                  onClick={onOpenAddonsDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Add-ons
                </button>
                <button
                  onClick={onOpenOtherChargesDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Other Charges
                </button>
              </>
            )}
          </div>
        </div>

        {/* Service name + price + edit pen */}
        <div className="flex items-center justify-between">
          <span className="text-gray-900 font-medium flex items-center gap-4">
            {serviceDetails.name || "No Service"}
            {/* {canEditService && (
              <button
                onClick={() => setEditPetServiceOpen(true)}
                className="text-blue-500 hover:text-indigo-600 transition-colors"
                title="Edit Service"
              >
                <FaPen className="w-3.5 h-3.5" />
              </button>
            )} */}
          </span>
          <span className="text-green-600 font-bold">
            ₹{Number(serviceDetails.price || 0).toFixed(0)}
          </span>
        </div>

        {/* Duration • Staff */}
        {isBoarding ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            {(boardingOrderItem?.duration || serviceDetails.durationDays) && (
              <span>
                <span className="font-medium text-gray-700">Duration:</span>{" "}
                {boardingOrderItem?.duration || serviceDetails.durationDays}{" "}
                Days
              </span>
            )}
          </div>
        ) : (
          <p className="flex items-center flex-wrap text-sm text-gray-500 mt-1 gap-2">
            {serviceDetails.duration && <span>{serviceDetails.duration}</span>}

            {serviceDetails.duration && staffName && (
              <span className="text-gray-400">•</span>
            )}

            {staffName && <span>{staffName}</span>}
          </p>
        )}

        {/* ADD-ONS */}
        {addons.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Add-on Services
            </h4>
            <div className="space-y-2">
              {addons.map((addon, aIdx) => (
                <div
                  key={aIdx}
                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center gap-2">
                    <FiCheck className="w-3 h-3 text-green-600" />
                    <span className="text-sm font-medium text-gray-800">
                      {addon.addon_name || addon.name || `Addon ${aIdx + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">
                      ₹
                      {Number(addon.addon_price || addon.price || 0).toFixed(2)}
                    </span>
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteAddon(aIdx)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove addon"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRODUCTS */}
        {products.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Products</h4>
            <div className="space-y-2">
              {products.map((product, pIdx) => {
                // Get values from product
                const mrpPrice = parseFloat(
                  product.mrp_price || product.mrp || product.sale_price || 0,
                );

                const effectivePrice = parseFloat(
                  product.effective_sale_price ??
                    product.unit_price ??
                    product.price ??
                    product.sale_price ??
                    product.mrp ??
                    product.total_price / (product.quantity || 1) ??
                    0,
                );

                const gstSlab = parseFloat(
                  product.gst_slab ?? product.tax_percentage ?? 0,
                );

                const priceWithoutTax =
                  gstSlab > 0
                    ? effectivePrice / (1 + gstSlab / 100)
                    : effectivePrice;

                const gstAmount =
                  gstSlab > 0 ? effectivePrice - priceWithoutTax : 0;

                const quantity = product.quantity || 1;
                const totalEffective = effectivePrice * quantity;
                const totalWithoutTax = priceWithoutTax * quantity;
                const totalGst = gstAmount * quantity;

                return (
                  <div
                    key={pIdx}
                    className="relative flex items-center justify-between p-2 pr-7 bg-blue-50 rounded-lg border border-blue-100"
                  >
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteProduct(pIdx)}
                        className="absolute top-1 right-1 text-red-500 hover:text-red-700"
                        title="Remove product"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    )}

                    <div className="flex items-start gap-2 flex-col">
                      <span className="text-sm font-medium text-gray-800 flex items-center gap-1">
                        <span>{product.product_name || product.name}</span>
                        {gstSlab > 0 && (
                          <span className="italic text-xs">
                            (gst {gstSlab}%)
                          </span>
                        )}
                        <span className="relative inline-flex group">
                          <span className="cursor-help text-gray-500 hover:text-gray-700">
                            <FaInfoCircle className="text-sm text-gray-500 cursor-pointer" />
                          </span>
                          <div className="invisible absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 rounded bg-gray-800 px-3 py-2 text-sm text-white opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 w-72">
                            <div className="whitespace-normal">
                              <strong className="block mb-1">
                                Product Details
                              </strong>
                              {product.description && (
                                <div>{product.description}</div>
                              )}
                              {effectivePrice > 0 && (
                                <div>
                                  Price: ₹{effectivePrice.toFixed(2)}
                                  {product.applied_discount && (
                                    <span className="italic text-sm ml-1">
                                      ({product.applied_discount.name}:{" "}
                                      {product.applied_discount.value}% off)
                                    </span>
                                  )}
                                </div>
                              )}
                              {mrpPrice > 0 && (
                                <div>MRP: ₹{mrpPrice.toFixed(2)}</div>
                              )}
                              {product.batches?.length > 0 && (
                                <div>
                                  Batch: {product.batches[0]?.batch_number}
                                </div>
                              )}
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                          </div>
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        {!readOnly && quantity > 1 && (
                          <button
                            onClick={() => onDecrementProduct(pIdx)}
                            className="text-black-500 hover:text-black-700"
                            title="Decrease quantity"
                          >
                            <FiMinus className="w-4 h-4" />
                          </button>
                        )}
                        {!readOnly && quantity === 1 && (
                          <button
                            onClick={() => onDeleteProduct(pIdx)}
                            className="text-black-500 hover:text-black-700"
                            title="Remove product"
                          >
                            <FiTrash2 className="w-3 h-3" />
                          </button>
                        )}
                        <span>{quantity}</span>
                        {!readOnly && (
                          <button
                            onClick={() => onIncrementProduct(pIdx)}
                            className="text-black-500 hover:text-black-700"
                            title="Increase quantity"
                          >
                            <FiPlus className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Price display with MRP strikethrough and tax breakdown */}
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-end">
                          {mrpPrice > 0 && effectivePrice < mrpPrice && (
                            <span className="text-gray-500 line-through text-sm">
                              ₹{mrpPrice.toFixed(2)}
                            </span>
                          )}
                          <span className="text-green-600 font-bold">
                            ₹{effectivePrice.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-green-600 font-bold min-w-[70px] text-right">
                          ₹{totalEffective.toFixed(2)}
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-green-600 font-bold">
                            ₹{totalWithoutTax.toFixed(2)}
                          </span>
                          <span className="text-gray-600 italic text-xs">
                            +₹{totalGst.toFixed(2)} taxes
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* OTHER CHARGES */}
        {otherCharges.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Other Charges
            </h4>
            <div className="space-y-2">
              {otherCharges.map((charge, cIdx) => (
                <div
                  key={cIdx}
                  className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-100"
                >
                  <span className="text-sm font-medium text-gray-800">
                    {charge.description || charge.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">
                      ₹{Number(charge.amount || 0).toFixed(2)}
                    </span>
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteOtherCharge(cIdx)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove charge"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(addons.length > 0 ||
          products.length > 0 ||
          otherCharges.length > 0) && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
            <span className="text-sm font-bold text-slate-700">
              Additional charges total: ₹{totalCharges.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Self-hosted edit drawer — no parent wiring required beyond passing data in */}
      {editPetServiceOpen && (
        <PetServiceCategoryDrawer
          isOpen={editPetServiceOpen}
          onClose={() => setEditPetServiceOpen(false)}
          onUpdate={(updated) => {
            setEditPetServiceOpen(false);
            if (updated && (updated.pet_name || updated.service_name)) {
              onServiceUpdated?.(updated);
            }
          }}
          clientId={clientId}
          clientName={clientName}
          clientNumber={clientPhone}
          itemData={{
            ...rawData,
            ref_id: rawData?.ref_id,
            vendor_pet_id: vendorPetId,
            service_id: rawData?.service_id,
            service_type_name:
              rawData?.service_type_name || rawData?.type || "grooming",
            order_id: orderId,
          }}
          orderId={orderId}
          editMode={true}
          isServiceEditMode={true}
          saleType="appointment"
        />
      )}
    </div>
  );
};

export default ServiceDetailsCard;
